'use client';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Link from 'next/link';
import { ButtonIcon, Typography } from '@workspace/ui/common';
import { cn, getLinkUrl, getLinkProps, isLinkActive, handleNavigationClick } from '@/app/lib';
import { Link as LinkType } from '@workspace/interfaces';
import type { IMegaMenuNavigationItemColumn, IMegaMenuNavigationItemColumnGrid } from '../../Header/interfaces';
import Image from 'next/image';

interface MobileMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  activePath?: string;
  onActivePathChange?: (newPath: string) => void;
  navigationItems?: IMegaMenuNavigationItemColumn[];
  primaryButton?: { label?: string; link?: any } | null;
}

interface NavigationLevel {
  title: string;
  items: (IMegaMenuNavigationItemColumn | IMegaMenuNavigationItemColumnGrid)[];
}

export const MobileMegaMenu: React.FC<MobileMegaMenuProps> = ({
  isOpen,
  onClose,
  activePath,
  onActivePathChange,
  navigationItems = [],
  primaryButton,
}) => {
  const prevNavigationItemsRef = useRef<IMegaMenuNavigationItemColumn[]>(navigationItems);
  const wasOpenRef = useRef(isOpen);

  const [currentLevel, setCurrentLevel] = useState(0);
  const [navigationStack, setNavigationStack] = useState<NavigationLevel[]>([
    { title: 'Menu', items: navigationItems || [] },
  ]);
  const [mounted, setMounted] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isClosing, setIsClosing] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);

  // Mount flag for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle opening and closing with animation
  useEffect(() => {
    if (isOpen) {
      // Reset navigation to root level when opening
      setNavigationStack([{ title: 'Menu', items: navigationItems || [] }]);
      setCurrentLevel(0);
      setHasNavigated(false);
      setIsGoingBack(false);

      setShouldRender(true);
      // Delay removing closing state to allow animation
      const timer = setTimeout(() => {
        setIsClosing(false);
      }, 10);
      return () => clearTimeout(timer);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setHasNavigated(false);
        setCurrentLevel(0);
        // Reset navigation stack after closing animation completes
        setNavigationStack([{ title: 'Menu', items: navigationItems || [] }]);
        setIsGoingBack(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender, navigationItems]);

  // Scroll to top only after an actual open -> closed transition
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (wasOpen && !isOpen) {
      const timer = setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Update navigation stack when items change
  useEffect(() => {
    const prevItems = prevNavigationItemsRef.current;
    const items = navigationItems || [];
    const hasChanged =
      prevItems.length !== items.length || prevItems.some((item, index) => item._key !== items[index]?._key);

    if (hasChanged) {
      setNavigationStack([{ title: 'Menu', items }]);
      setCurrentLevel(0);
      prevNavigationItemsRef.current = items;
    }
  }, [navigationItems]);

  const handleBackNavigation = () => {
    if (currentLevel > 0) {
      setHasNavigated(true);
      setIsGoingBack(true);
      setCurrentLevel((lvl) => lvl - 1);
      setNavigationStack((prev) => prev.slice(0, -1));
    }
  };

  const handleChevronClick = (item: IMegaMenuNavigationItemColumn, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const children = item?.childrenColumn || item?.childrenGrid;
    if (!children?.length || !item?.label) return;

    setHasNavigated(true);
    setIsGoingBack(false);
    setNavigationStack((prev) => [...prev, { title: item.label, items: children }]);
    setCurrentLevel((lvl) => lvl + 1);
  };

  const handleItemClick = (link?: LinkType) => {
    if (link && onActivePathChange) {
      handleNavigationClick(link, onActivePathChange);
      onClose();
    }
  };

  const handleLinkClick = (
    e: React.MouseEvent,
    item: IMegaMenuNavigationItemColumn | IMegaMenuNavigationItemColumnGrid,
    hasChildren: boolean,
    isColumnItem: boolean,
    itemLink: LinkType | undefined,
    linkUrl: string,
  ) => {
    const isSubmenuOnly = hasChildren && !itemLink?.internalReference && !itemLink?.url;
    const isLinkItem = isColumnItem && itemLink;

    if (isSubmenuOnly) {
      e.preventDefault();
      handleChevronClick(item as IMegaMenuNavigationItemColumn, e);
    } else if (isLinkItem) {
      handleItemClick(itemLink);
      onClose();
    } else {
      onActivePathChange?.(linkUrl);
      onClose();
    }
  };

  const currentNavigationLevel = navigationStack[currentLevel];
  const currentItems = currentNavigationLevel?.items || [];

  const renderAdBanner = () => {
    if (!navigationItems || navigationItems.length === 0) return null;

    const parentItem = navigationItems.find((item) => item?.label === currentNavigationLevel?.title);
    const adBanner = parentItem?.advertisingBanner;

    if (!adBanner?.isEnabled || !adBanner.image) return null;

    return (
      <div className="p-md">
        <Link
          href={adBanner.link?.href || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="overflow-hidden transition-opacity duration-200 hover:opacity-90 flex justify-center items-center "
        >
          <Image
            src={adBanner.image}
            alt="Advertisement"
            className="!h-63 !w-63 object-cover"
            width={252}
            height={252}
          />
        </Link>

        {adBanner?.label && (
          <Link href={adBanner.link?.href || '#'} target="_blank" className="mx-auto block w-63">
            <Typography variant="link" className="cursor-pointer text-sm no-underline font-normal text-secondary-400">
              {adBanner.label}
            </Typography>
          </Link>
        )}
      </div>
    );
  };

  if (!mounted || !shouldRender) return null;

  const isPrimaryActive = Boolean(primaryButton?.link && isLinkActive(primaryButton?.link, activePath));

  // Render the panel in a portal to escape any overflow-hidden parent wrappers
  return ReactDOM.createPortal(
    <>
      {/* Backdrop that blocks interactions behind the menu */}
      <div
        className={cn(
          'fixed inset-0 z-[55] bg-black/40 pointer-events-auto transition-opacity duration-300',
          isOpen && !isClosing ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden="true"
      />

      {/* Panel: receives all pointer events */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 top-18 z-[60] w-4/5 h-dvh bg-white shadow-lg pointer-events-auto transition-all duration-300',
          isOpen && !isClosing ? 'translate-x-0 opacity-100' : 'translate-x-[20%] opacity-0',
        )}
        // Make sure events inside the panel don't bubble to any global touchmove preventers
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {/* Sliding content container */}
        <div className="relative h-full overflow-hidden">
          <div
            key={currentLevel}
            className={cn(
              'absolute inset-0 transition-transform duration-300 ease-out',
              hasNavigated && (isGoingBack ? 'animate-slideInFromLeft' : 'animate-slideInFromRight'),
            )}
          >
            {/* Flex column with min-h-0 so inner content can scroll */}
            <div className="flex h-full min-h-0 flex-col">
              {currentLevel > 0 && (
                <div className="flex h-16 shrink-0 items-center border-b border-secondary-100 px-6 py-4">
                  <ButtonIcon
                    onClick={handleBackNavigation}
                    iconName="ChevronLeft"
                    variant="link"
                    iconSize={24}
                    className="-ml-xs"
                  />
                  {currentNavigationLevel?.title && currentNavigationLevel.title !== 'Menu' && (
                    <Typography
                      variant="h2"
                      className={cn(
                        'text-lg leading-[26px] font-bold text-secondary-400 !font-openSans',
                        currentLevel === 2 && 'text-xl leading-8',
                      )}
                    >
                      {currentNavigationLevel.title}
                    </Typography>
                  )}
                </div>
              )}

              {/* The ONLY scrollable area */}
              <div className="flex-1 min-h-0 relative overflow-hidden">
                <div className="absolute inset-0 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch] [touch-action:pan-y] pb-16">
                  <ul>
                    {currentItems.map((item) => {
                      const itemKey = item?._key;
                      const itemLabel = item?.label;

                      if (!itemKey || !itemLabel) return null;

                      const isColumnItem = 'childrenColumn' in item || 'childrenGrid' in item;

                      let hasChildren = false;
                      let itemLink: LinkType | undefined;
                      let linkUrl = '#';

                      if (isColumnItem) {
                        const columnItem = item as IMegaMenuNavigationItemColumn;
                        hasChildren = Boolean(
                          (columnItem.childrenColumn && columnItem.childrenColumn.length > 0) ||
                          (columnItem.childrenGrid && columnItem.childrenGrid.length > 0),
                        );
                        itemLink = columnItem.link;
                        linkUrl = itemLink ? getLinkUrl(itemLink) : '#';
                      } else {
                        const gridItem = item as IMegaMenuNavigationItemColumnGrid;
                        const primaryLinkUrl = gridItem.link?.url?.trim();
                        const secondaryLinkUrl = gridItem.link ? getLinkUrl(gridItem.link) : undefined;
                        linkUrl = primaryLinkUrl || secondaryLinkUrl || '#';
                      }
                      const isActive = isColumnItem && itemLink && isLinkActive(itemLink, activePath);

                      return (
                        <li key={itemKey} className="border-b border-secondary-100">
                          <div className={cn('flex items-center', currentLevel !== 0 && 'pl-lg')}>
                            <div className="flex-1">
                              <Link
                                href={linkUrl}
                                {...(isColumnItem && itemLink ? getLinkProps(itemLink) : {})}
                                className={cn(
                                  'block px-6 py-4 text-left transition-colors duration-200 leading-md !font-openSans',
                                  isActive ? 'text-primary' : 'text-secondary-400 hover:text-primary-500',
                                  currentLevel === 0 ? 'text-lg font-bold' : '!font-openSans text-xl font-normal',
                                )}
                                onClick={(e) => handleLinkClick(e, item, hasChildren, isColumnItem, itemLink, linkUrl)}
                              >
                                {itemLabel}
                              </Link>
                            </div>

                            {hasChildren && (
                              <ButtonIcon
                                onClick={(e) => handleChevronClick(item as IMegaMenuNavigationItemColumn, e)}
                                iconName="ChevronRight"
                                iconSize={24}
                                variant="link"
                                className="mr-md"
                                size="sm"
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                    {primaryButton && primaryButton.link && currentLevel === 0 && (
                      <li className="border-b border-secondary-100">
                        {/* You can remove this wrapper onClick and only keep the Link's onClick if you prefer */}
                        <div className="flex-1">
                          <Link
                            href={getLinkUrl(primaryButton.link)}
                            {...(primaryButton.link.type === 'external'
                              ? {
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                }
                              : {})}
                            className={cn(
                              'block px-6 py-4 text-left transition-colors duration-200 leading-md !font-openSans',
                              isPrimaryActive ? 'text-primary' : 'text-secondary-400 hover:text-primary-500',
                              currentLevel === 0 ? 'text-lg font-bold' : '!font-openSans text-xl font-normal',
                            )}
                            onClick={() => {
                              if (primaryButton?.link) {
                                const linkUrl = getLinkUrl(primaryButton.link);
                                onActivePathChange?.(linkUrl);
                              }
                              onClose();
                            }}
                          >
                            {primaryButton.label || ''}
                          </Link>
                        </div>
                      </li>
                    )}
                  </ul>

                  {currentLevel > 0 && renderAdBanner()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
